/**
 * Team list component with dual view support (table and cards).
 * Stores view preference in localStorage for persistence.
 */

import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TeamMember } from '../../types/company';
import TeamMemberCard from './TeamMemberCard';
import TeamMemberRow from './TeamMemberRow';
import TeamEmptyState from './TeamEmptyState';

type ViewMode = 'table' | 'card';

const VIEW_PREFERENCE_KEY = 'safaqat24.team.viewMode';

interface TeamListProps {
  members: TeamMember[];
  onRoleChange?: (member: TeamMember) => void;
  onRemove?: (member: TeamMember) => void;
  onInvite: () => void;
}

export default function TeamList({
  members,
  onRoleChange,
  onRemove,
  onInvite,
}: TeamListProps) {
  const { t, i18n } = useTranslation('team');
  const isRtl = i18n.dir() === 'rtl';

  // Initialize view mode from localStorage
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const stored = localStorage.getItem(VIEW_PREFERENCE_KEY);
    return stored === 'card' || stored === 'table' ? stored : 'table';
  });

  // Persist view mode to localStorage
  useEffect(() => {
    localStorage.setItem(VIEW_PREFERENCE_KEY, viewMode);
  }, [viewMode]);

  // Sort members alphabetically by displayName
  const sortedMembers = useMemo(() => {
    return [...members].sort((a, b) =>
      a.displayName.localeCompare(b.displayName, i18n.language)
    );
  }, [members, i18n.language]);

  // Show empty state when no members
  if (members.length === 0) {
    return <TeamEmptyState onInvite={onInvite} />;
  }

  return (
    <div>
      {/* Header with view toggle */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">
          {t('membersCount', {
            count: members.length,
            defaultValue_one: '{{count}} member',
            defaultValue_other: '{{count}} members',
          })}
        </p>

        {/* View Toggle Buttons */}
        <div className="inline-flex items-center bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setViewMode('table')}
            className={`
              p-2 rounded-md transition-colors
              ${
                viewMode === 'table'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }
            `}
            title={t('common:tableView', 'Table View')}
            aria-label="Table view"
          >
            <i className="ri-list-check text-lg" />
          </button>
          <button
            onClick={() => setViewMode('card')}
            className={`
              p-2 rounded-md transition-colors
              ${
                viewMode === 'card'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }
            `}
            title={t('common:cardView', 'Card View')}
            aria-label="Card view"
          >
            <i className="ri-grid-line text-lg" />
          </button>
        </div>
      </div>

      {/* Content based on view mode */}
      {viewMode === 'table' ? (
        // No overflow-hidden on the outer wrapper — it would clip the row
        // actions dropdown when it pops below the last row. The inner table
        // already has divide-y separators; the thead background fills the
        // top corners cleanly, so the rounded-lg edges still read correctly.
        <div className="bg-white rounded-lg shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${
                    isRtl ? 'text-right' : 'text-left'
                  }`}
                >
                  {t('common:name', 'Name')}
                </th>
                <th
                  scope="col"
                  className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${
                    isRtl ? 'text-right' : 'text-left'
                  }`}
                >
                  {t('common:email', 'Email')}
                </th>
                <th
                  scope="col"
                  className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${
                    isRtl ? 'text-right' : 'text-left'
                  }`}
                >
                  {t('common:role', 'Role')}
                </th>
                <th
                  scope="col"
                  className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${
                    isRtl ? 'text-right' : 'text-left'
                  }`}
                >
                  {t('memberSince', 'Member since')}
                </th>
                <th
                  scope="col"
                  className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${
                    isRtl ? 'text-right' : 'text-left'
                  }`}
                >
                  {t('listings', 'Listings')}
                </th>
                <th scope="col" className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedMembers.map((member) => (
                <TeamMemberRow
                  key={member.id}
                  member={member}
                  onRoleChange={onRoleChange}
                  onRemove={onRemove}
                  isCurrentUser={member.isCurrentUser}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        // Card grid view - responsive: 1 col mobile, 2 col tablet, 3 col desktop
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sortedMembers.map((member) => (
            <TeamMemberCard
              key={member.id}
              member={member}
              onRoleChange={onRoleChange}
              onRemove={onRemove}
              isCurrentUser={member.isCurrentUser}
            />
          ))}
        </div>
      )}
    </div>
  );
}
